import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  CheckCircle2, Circle, Phone, Sparkles, CalendarClock,
  AlertCircle, Plus, Trash2, Clock, User as UserIcon, Package,
  Settings2, History, X, ChevronUp, ChevronDown,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  login_check: { color: 'bg-amber-100 text-amber-900 border-amber-300', label: 'Login', icon: UserIcon },
  clean_shelves: { color: 'bg-sky-100 text-sky-900 border-sky-300', label: 'Shelves', icon: Sparkles },
  expiry_check: { color: 'bg-rose-100 text-rose-900 border-rose-300', label: 'Expiry', icon: AlertCircle },
  call_customer: { color: 'bg-emerald-100 text-emerald-900 border-emerald-300', label: 'Call Customer', icon: Phone },
  stocktake: { color: 'bg-indigo-100 text-indigo-900 border-indigo-300', label: 'Stocktake', icon: Package },
  general: { color: 'bg-slate-100 text-slate-900 border-slate-300', label: 'Task', icon: CheckCircle2 },
};

const PRIORITY_META = {
  high: { color: 'bg-red-600 text-white', label: 'HIGH' },
  medium: { color: 'bg-amber-500 text-white', label: 'MED' },
  low: { color: 'bg-slate-400 text-white', label: 'LOW' },
};

const StaffTasks = () => {
  const { getAuthHeader, user, activeStaff, hasPermission } = useAuth();
  const { selectedStore, storeName } = useStore();
  const [data, setData] = useState({ tasks: [], overdue: [], today: '', weekday: '', on_shift_now: null, today_shifts: [] });
  const [loading, setLoading] = useState(true);
  const [completeDialog, setCompleteDialog] = useState({ open: false, task: null });
  const [completeForm, setCompleteForm] = useState({ name: '', notes: '' });
  const [addDialog, setAddDialog] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', description: '', priority: 'medium' });
  const [bayDialog, setBayDialog] = useState(false);
  const [historyBay, setHistoryBay] = useState(null);
  const [reorderBusy, setReorderBusy] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/staff/tasks/today?store=${selectedStore}`, { headers: getAuthHeader() });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, selectedStore]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const openCompleteDialog = (task) => {
    // PREFER the PIN-logged staff (activeStaff) over the owner login (user.username).
    // If neither matches the on-shift name, leave it BLANK so staff MUST type
    // their own name — prevents tasks being silently attributed to the owner.
    const onShiftName = data?.on_shift_now?.staff_name || '';
    const pinName = activeStaff?.name || activeStaff?.first_name || '';
    const ownerName = user?.name || user?.username || '';

    let prefill = '';
    if (pinName) {
      prefill = pinName;
    } else if (onShiftName) {
      // No PIN session — default to whoever the roster says is on shift right now.
      prefill = onShiftName;
    } else if (ownerName) {
      // Last resort — but only if it isn't the owner's full name (owner accounts
      // are usually e.g. "Ritu Tomar"; we don't want to silently mark Karen's
      // work as done by Ritu).
      prefill = ownerName;
    }
    setCompleteForm({ name: prefill, notes: '' });
    setCompleteDialog({ open: true, task });
  };

  const submitComplete = async () => {
    const t = completeDialog.task;
    if (!t) return;
    const trimmed = completeForm.name.trim();
    if (!trimmed) return toast.error('Type your name so we know who did it');
    // Block staff from blindly accepting the owner's pre-fill when the roster
    // clearly expects someone else right now — they must change it.
    const onShiftName = (data?.on_shift_now?.staff_name || '').toLowerCase();
    const ownerName = (user?.name || user?.username || '').toLowerCase();
    if (
      onShiftName &&
      ownerName &&
      onShiftName !== ownerName &&
      trimmed.toLowerCase() === ownerName &&
      !window.confirm(
        `The roster says ${data.on_shift_now.staff_name} is on shift right now — not ${user?.name || user?.username}.\n\n` +
        `Are you sure YOU (${trimmed}) did this task and not ${data.on_shift_now.staff_name}?`
      )
    ) {
      return;
    }
    try {
      await axios.post(`${API_URL}/staff/tasks/${t.id}/complete`, {
        completed_by_name: trimmed,
        notes: completeForm.notes,
      }, { headers: getAuthHeader() });
      toast.success(`Done ✓ — ${t.title}`);
      setCompleteDialog({ open: false, task: null });
      fetchTasks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not mark complete');
    }
  };

  const undoComplete = async (task) => {
    try {
      await axios.post(`${API_URL}/staff/tasks/${task.id}/uncomplete`, {}, { headers: getAuthHeader() });
      fetchTasks();
    } catch {
      toast.error('Could not undo');
    }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    try {
      await axios.delete(`${API_URL}/staff/tasks/${task.id}`, { headers: getAuthHeader() });
      fetchTasks();
    } catch {
      toast.error('Could not delete');
    }
  };

  const addCustomTask = async () => {
    if (!newTask.title.trim()) return toast.error('Title required');
    try {
      await axios.post(`${API_URL}/staff/tasks`, {
        store: selectedStore,
        title: newTask.title.trim(),
        description: newTask.description,
        category: 'general',
        priority: newTask.priority,
      }, { headers: getAuthHeader() });
      toast.success('Task added');
      setNewTask({ title: '', description: '', priority: 'medium' });
      setAddDialog(false);
      fetchTasks();
    } catch {
      toast.error('Could not add task');
    }
  };

  const renderTask = (task) => {
    const cat = CATEGORY_META[task.category] || CATEGORY_META.general;
    const pri = PRIORITY_META[task.priority] || PRIORITY_META.medium;
    const Icon = cat.icon;
    return (
      <div
        key={task.id}
        className={`rounded-md border-2 p-3 transition-all ${task.completed ? 'bg-emerald-50 border-emerald-200 opacity-60' : task.is_overdue ? 'bg-red-50 border-red-300' : 'bg-white border-slate-200 hover:border-slate-300'}`}
        data-testid={`task-${task.id}`}
      >
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => task.completed ? undoComplete(task) : openCompleteDialog(task)}
            className="mt-0.5 shrink-0"
            data-testid={`task-toggle-${task.id}`}
            title={task.completed ? 'Mark as not done' : 'Mark as done'}
          >
            {task.completed
              ? <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              : <Circle className="w-7 h-7 text-slate-300 hover:text-emerald-500" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 flex-wrap">
              <p className={`font-bold text-base ${task.completed ? 'line-through text-slate-500' : 'text-slate-900'}`}>{task.title}</p>
              <Badge className={`text-[10px] font-bold ${pri.color}`}>{pri.label}</Badge>
              <Badge variant="outline" className={`text-[10px] ${cat.color}`}><Icon className="w-3 h-3 mr-1 inline" />{cat.label}</Badge>
              {task.is_overdue && <Badge className="bg-red-600 text-white text-[10px] font-bold">OVERDUE</Badge>}
            </div>
            {task.description && (
              <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{task.description}</p>
            )}
            {task.customer_phone && (
              <a href={`tel:${task.customer_phone}`} className="inline-flex items-center gap-1 text-sm font-bold text-blue-600 hover:underline mt-1" data-testid={`task-call-${task.id}`}>
                <Phone className="w-3.5 h-3.5" /> {task.customer_phone}
              </a>
            )}
            {task.completed && task.completed_by_name && (
              <p className="text-xs text-emerald-700 mt-1.5">
                ✓ Done by <b>{task.completed_by_name}</b>
                {task.completed_at && ` · ${new Date(task.completed_at).toLocaleString('en-NZ', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}`}
                {task.notes && ` · "${task.notes}"`}
              </p>
            )}
          </div>
          {!task.is_auto_seed && (
            <Button variant="ghost" size="icon" onClick={() => deleteTask(task)} className="h-8 w-8 text-slate-400 hover:text-red-600 shrink-0" title="Delete task">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          {canReorder && !task.completed && task.category !== 'call_customer' && !task.is_overdue && (
            <div className="flex flex-col shrink-0 -my-1">
              <Button variant="ghost" size="icon" onClick={() => moveTask(task, -1)} disabled={reorderBusy || dailyIndex(task) === 0} className="h-6 w-7 text-slate-400 hover:text-slate-900" title="Move up" data-testid={`task-up-${task.id}`}>
                <ChevronUp className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => moveTask(task, 1)} disabled={reorderBusy || dailyIndex(task) === openDaily.length - 1} className="h-6 w-7 text-slate-400 hover:text-slate-900" title="Move down" data-testid={`task-down-${task.id}`}>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Owner/admin can put the most important tasks on top; the order is saved per store and staff see it the same way
  const canReorder = hasPermission('admin');
  const openDaily = data.tasks.filter(t => t.category !== 'call_customer' && !t.completed);
  const dailyIndex = (task) => openDaily.findIndex(t => t.id === task.id);
  const moveTask = async (task, dir) => {
    const idx = dailyIndex(task);
    const to = idx + dir;
    if (idx < 0 || to < 0 || to >= openDaily.length) return;
    const next = [...openDaily];
    [next[idx], next[to]] = [next[to], next[idx]];
    const doneDaily = data.tasks.filter(t => t.category !== 'call_customer' && t.completed);
    setData(d => ({ ...d, tasks: [...d.tasks.filter(t => t.category === 'call_customer'), ...next, ...doneDaily] }));
    setReorderBusy(true);
    try {
      await axios.put(`${API_URL}/staff/tasks/order`, { store: selectedStore, ordered_keys: next.map(t => t.order_key) }, { headers: getAuthHeader() });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the order');
      fetchTasks();
    } finally { setReorderBusy(false); }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;

  const callTasks = data.tasks.filter(t => t.category === 'call_customer');
  const dailyTasks = data.tasks.filter(t => t.category !== 'call_customer');
  const completed = data.tasks.filter(t => t.completed).length;
  const total = data.tasks.length;

  return (
    <div className="space-y-6" data-testid="staff-tasks-page">
      {/* Header + Schedule */}
      <div className="rounded-md border-2 border-slate-900 bg-slate-900 text-white p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
              Today's Checklist · {data.weekday}
            </h1>
            <p className="text-slate-300 text-sm">
              {storeName(selectedStore)} · {data.today}
              {' · '}
              <span className="text-amber-300">{completed}/{total} done</span>
            </p>
          </div>
          {data.on_shift_now && (
            <div className="text-right">
              <p className="text-xs uppercase tracking-wider text-slate-400">On shift now</p>
              <p className="text-xl font-bold text-amber-300">{data.on_shift_now.staff_name}</p>
              <p className="text-xs text-slate-300">{data.on_shift_now.start} – {data.on_shift_now.end}</p>
            </div>
          )}
        </div>
        {/* Today's shifts strip */}
        {data.today_shifts && data.today_shifts.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-700 flex items-center gap-2 flex-wrap text-sm">
            <CalendarClock className="w-4 h-4 text-slate-400" />
            <span className="text-slate-400 text-xs uppercase tracking-wider mr-1">Roster</span>
            {data.today_shifts.map((s, idx) => {
              const isNow = data.on_shift_now && data.on_shift_now.start === s.start && data.on_shift_now.staff_name === s.staff_name;
              return (
                <span key={idx} className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${isNow ? 'bg-amber-300 text-slate-900 font-bold' : 'bg-slate-800 text-slate-200'}`}>
                  <Clock className="w-3 h-3" /> {s.start}–{s.end} {s.staff_name}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Overdue customer-call tasks first — these are the resveratrol-class misses */}
      {data.overdue && data.overdue.length > 0 && (
        <Card className="border-2 border-red-400">
          <CardHeader className="bg-red-50 border-b border-red-200 py-3">
            <CardTitle className="font-heading text-base font-bold uppercase text-red-700 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> Overdue — Don't forget these ({data.overdue.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            {data.overdue.map(renderTask)}
          </CardContent>
        </Card>
      )}

      {/* Customer call tasks (auto-generated when PO received) */}
      {callTasks.length > 0 && (
        <Card className="border-2 border-emerald-300">
          <CardHeader className="bg-emerald-50 border-b border-emerald-200 py-3">
            <CardTitle className="font-heading text-base font-bold uppercase text-emerald-800 flex items-center gap-2">
              <Phone className="w-5 h-5" /> Customers To Call — Their Orders Arrived ({callTasks.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            {callTasks.map(renderTask)}
          </CardContent>
        </Card>
      )}

      {/* Daily checklist */}
      <Card>
        <CardHeader className="border-b py-3 flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-base font-bold uppercase flex items-center gap-2">
            <Sparkles className="w-5 h-5" /> Daily Checklist
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setBayDialog(true)} data-testid="manage-bays-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Bays
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAddDialog(true)} data-testid="add-task-btn">
              <Plus className="w-4 h-4 mr-1" /> Add task
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-3 space-y-2">
          {dailyTasks.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-6">No tasks for today. Add one to get started.</p>
          ) : (
            dailyTasks.map(renderTask)
          )}
        </CardContent>
      </Card>

      {/* Complete dialog */}
      <Dialog open={completeDialog.open} onOpenChange={(o) => !o && setCompleteDialog({ open: false, task: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Mark as done</DialogTitle>
          </DialogHeader>
          {completeDialog.task && (
            <div className="space-y-3">
              <p className="font-medium text-slate-700">{completeDialog.task.title}</p>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-500">Who did this? (your name, not the owner's)</label>
                {/* Quick-pick staff buttons — common names + whoever's on shift */}
                <div className="flex flex-wrap gap-1.5 mt-1.5 mb-1.5">
                  {Array.from(new Set([
                    data?.on_shift_now?.staff_name,
                    activeStaff?.name,
                    'Katie', 'Karen', 'Ritu', 'Tina',
                  ].filter(Boolean))).map(name => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setCompleteForm(prev => ({ ...prev, name }))}
                      className={`px-3 py-1 rounded border text-sm font-bold ${completeForm.name.toLowerCase() === name.toLowerCase() ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'}`}
                      data-testid={`pick-staff-${name.toLowerCase()}`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
                <Input
                  value={completeForm.name}
                  onChange={(e) => setCompleteForm({ ...completeForm, name: e.target.value })}
                  placeholder="Or type your name…"
                  autoFocus
                  data-testid="complete-name"
                />
                {data?.on_shift_now?.staff_name && (
                  <p className="text-xs text-slate-500 mt-1">Roster says <b>{data.on_shift_now.staff_name}</b> is on shift now.</p>
                )}
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-slate-500">Notes (optional)</label>
                <Input
                  value={completeForm.notes}
                  onChange={(e) => setCompleteForm({ ...completeForm, notes: e.target.value })}
                  placeholder="e.g. Lifestream + Pure Encapsulations checked"
                  data-testid="complete-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteDialog({ open: false, task: null })}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submitComplete} data-testid="complete-submit">
              <CheckCircle2 className="w-4 h-4 mr-1" /> Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add task dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Add Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="What needs doing?"
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              autoFocus
              data-testid="new-task-title"
            />
            <Input
              placeholder="Details (optional)"
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
            />
            <div className="flex gap-2">
              {['low', 'medium', 'high'].map(p => (
                <Button
                  key={p}
                  type="button"
                  variant={newTask.priority === p ? 'default' : 'outline'}
                  onClick={() => setNewTask({ ...newTask, priority: p })}
                  className={newTask.priority === p ? (PRIORITY_META[p].color) : ''}
                  size="sm"
                >
                  {PRIORITY_META[p].label}
                </Button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)}>Cancel</Button>
            <Button onClick={addCustomTask} data-testid="new-task-submit">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManageBaysDialog
        open={bayDialog}
        onClose={() => setBayDialog(false)}
        store={selectedStore}
        getAuthHeader={getAuthHeader}
        onOpenHistory={(bayName) => setHistoryBay(bayName)}
      />

      <BayHistoryDialog
        bayName={historyBay}
        store={selectedStore}
        getAuthHeader={getAuthHeader}
        onClose={() => setHistoryBay(null)}
      />
    </div>
  );
};

// ---------------- ManageBaysDialog ----------------
function ManageBaysDialog({ open, onClose, store, getAuthHeader, onOpenHistory }) {
  const { storeName } = useStore();
  const [bays, setBays] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // index being renamed
  const [editingValue, setEditingValue] = useState('');

  const fetchBays = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/staff/task-bays?store=${store}`, { headers: getAuthHeader() });
      setBays(res.data.bays || []);
      setSuggested(res.data.suggested_defaults || []);
    } catch {
      toast.error('Could not load bays');
    } finally {
      setLoading(false);
    }
  }, [open, store, getAuthHeader]);

  useEffect(() => { fetchBays(); }, [fetchBays]);

  const applyDefaults = async () => {
    if (!window.confirm(`Load the Wellness Store layout (${suggested.length} bays)? This will replace your current list for ${store}.`)) return;
    try {
      await axios.put(`${API_URL}/staff/task-bays`, { store, names: suggested }, { headers: getAuthHeader() });
      toast.success(`Loaded ${suggested.length} bays`);
      fetchBays();
    } catch {
      toast.error('Could not apply defaults');
    }
  };

  const addBay = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    try {
      await axios.post(`${API_URL}/staff/task-bays`, { store, name: trimmed }, { headers: getAuthHeader() });
      setNewName('');
      fetchBays();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add bay');
    }
  };

  const deleteBay = async (name) => {
    if (!window.confirm(`Remove bay "${name}"? History is kept in the audit log.`)) return;
    try {
      await axios.delete(`${API_URL}/staff/task-bays/${encodeURIComponent(name)}?store=${store}`, { headers: getAuthHeader() });
      fetchBays();
    } catch {
      toast.error('Could not delete bay');
    }
  };

  const startRename = (idx, name) => { setEditing(idx); setEditingValue(name); };
  const saveRename = async (oldName) => {
    const trimmed = editingValue.trim();
    if (!trimmed || trimmed === oldName) { setEditing(null); return; }
    // Replace whole list with the renamed entry — preserves history for surviving names
    const newNames = bays.map(b => b.name === oldName ? trimmed : b.name);
    try {
      await axios.put(`${API_URL}/staff/task-bays`, { store, names: newNames }, { headers: getAuthHeader() });
      setEditing(null);
      fetchBays();
    } catch {
      toast.error('Could not rename');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="manage-bays-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase flex items-center gap-2">
            <Settings2 className="w-5 h-5" /> Manage Bays · {storeName(store)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            These are the shelf bays the daily cleaning task rotates through. The bay
            with the <b>oldest</b> "last cleaned" date is auto-picked each day.
          </p>

          {loading ? (
            <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
          ) : bays.length === 0 ? (
            <div className="rounded-md border-2 border-dashed border-slate-300 p-4 text-center" data-testid="empty-bays">
              <p className="text-sm text-slate-600 mb-3">No bays configured yet for {storeName(store)}.</p>
              <Button onClick={applyDefaults} className="bg-emerald-600 hover:bg-emerald-700" data-testid="apply-defaults-btn">
                Use Wellness Store layout ({suggested.length} bays)
              </Button>
            </div>
          ) : (
            <div className="space-y-1.5" data-testid="bay-list">
              {bays.map((b, idx) => (
                <div key={b.name} className="flex items-center gap-2 px-2 py-1.5 rounded border border-slate-200 hover:bg-slate-50" data-testid={`bay-row-${idx}`}>
                  {editing === idx ? (
                    <Input
                      value={editingValue}
                      onChange={(e) => setEditingValue(e.target.value)}
                      onBlur={() => saveRename(b.name)}
                      onKeyDown={(e) => { if (e.key === 'Enter') saveRename(b.name); if (e.key === 'Escape') setEditing(null); }}
                      autoFocus
                      className="flex-1"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => startRename(idx, b.name)}
                      className="flex-1 text-left font-medium text-slate-800 hover:text-emerald-700"
                      title="Click to rename"
                    >
                      {b.name}
                    </button>
                  )}
                  <span className="text-xs text-slate-500 shrink-0">
                    {b.last_cleaned_at
                      ? `Cleaned ${new Date(b.last_cleaned_at).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short' })} by ${b.last_cleaned_by || '—'}`
                      : <span className="text-amber-600">Never cleaned</span>}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-slate-400 hover:text-slate-700"
                    onClick={() => onOpenHistory(b.name)}
                    title="View cleaning history"
                    data-testid={`bay-history-${idx}`}
                  >
                    <History className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-slate-400 hover:text-red-600"
                    onClick={() => deleteBay(b.name)}
                    title="Remove bay"
                    data-testid={`bay-delete-${idx}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t">
            <Input
              placeholder="New bay name (e.g. Bay 6 — Nutralife)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addBay(); }}
              data-testid="new-bay-input"
            />
            <Button onClick={addBay} disabled={!newName.trim()} data-testid="add-bay-btn">
              <Plus className="w-4 h-4 mr-1" /> Add bay
            </Button>
          </div>

          {bays.length > 0 && (
            <p className="text-[11px] text-slate-400 pt-1">
              Tip: click a bay name to rename it. Renaming keeps the cleaning history.
              The history icon shows who cleaned what and when.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- BayHistoryDialog ----------------
function BayHistoryDialog({ bayName, store, getAuthHeader, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!bayName) return;
    setLoading(true);
    axios.get(
      `${API_URL}/staff/task-bays/${encodeURIComponent(bayName)}/history?store=${store}`,
      { headers: getAuthHeader() }
    )
      .then((res) => setHistory(res.data.history || []))
      .catch(() => toast.error('Could not load history'))
      .finally(() => setLoading(false));
  }, [bayName, store, getAuthHeader]);

  return (
    <Dialog open={!!bayName} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="bay-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase flex items-center gap-2">
            <History className="w-5 h-5" /> {bayName} · Cleaning History
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-slate-400 py-6 text-center">Loading…</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center">
            No cleaning history yet for this bay. Once staff complete the daily cleaning
            task for it, the record will appear here.
          </p>
        ) : (
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={i} className="rounded-md border border-slate-200 p-3 bg-slate-50" data-testid={`history-row-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-bold text-slate-800">{h.completed_by_name}</p>
                  <p className="text-xs text-slate-500">
                    {new Date(h.completed_at).toLocaleString('en-NZ', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                {h.notes && <p className="text-sm text-slate-600 mt-1 italic">"{h.notes}"</p>}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default StaffTasks;
